import type { PageRoute } from "../seo/document";
import { articleNode, breadcrumbNode, faqPageNode, graph, type Faq } from "../seo/schema";
import {
  accentRailMarkup,
  arrowList,
  ctaBand,
  faqSection,
  paragraphs,
  siteFooter,
  topNav
} from "./layout";

type StepBlock = {
  heading: string;
  copy: readonly string[];
  bullets?: readonly string[];
  examples?: readonly { title: string; reply: string }[];
  template?: string;
};

type GuidePageSpec = {
  path: string;
  title: string;
  h1: string;
  keyword: string;
  description: string;
  h2: string;
  subhead: string;
  intro: readonly string[];
  steps: readonly StepBlock[];
  faqs: readonly Faq[];
};

function renderStepBlocks(steps: readonly StepBlock[]): string {
  return steps
    .map((step, index) => {
      const sectionClass = index % 2 === 0 ? "section" : "section section-soft";
      const bulletMarkup = step.bullets
        ? `
          <div class="offer-block">
            <p class="block-label">Checklist</p>
            <ul>${arrowList(step.bullets)}</ul>
          </div>`
        : "";
      const exampleMarkup =
        step.examples && step.examples.length > 0
          ? `
          <div class="offer-block">
            <p class="block-label">Example replies</p>
            ${step.examples
            .map((example) => `<h3>${example.title}</h3><p>${example.reply}</p>`)
            .join("")}
          </div>`
          : "";
      const templateMarkup = step.template
        ? `
          <div class="offer-block">
            <p class="block-label">Template</p>
            <p>${step.template}</p>
          </div>`
        : "";

      return `
        <section class="${sectionClass}" data-screen-label="${step.heading}">
          <div class="section-heading">
            ${accentRailMarkup()}
            <h2>${step.heading}</h2>
          </div>
          ${paragraphs(step.copy)}
          ${bulletMarkup}
          ${templateMarkup}
          ${exampleMarkup}
        </section>`;
    })
    .join("");
}

function renderGuidePage(data: GuidePageSpec, assetBase: string): string {
  return `
    <main data-screen-label="Guide">
      ${topNav(assetBase)}

      <section class="section page-hero" data-screen-label="Hero">
        <div class="page-hero-inner">
          ${accentRailMarkup()}
          <p class="eyebrow">Guide</p>
          <h1>${data.h1}</h1>
          <p class="hero-subhead">${data.subhead}</p>
          <div class="actions">
            <a class="button button-primary" href="/google-review-management">Get review management</a>
            <a class="button button-outline" href="/#services">See Foxmark packs</a>
          </div>
          <p class="proof-line">$149/month · No contracts · Cancel anytime</p>
        </div>
      </section>

      <section class="section" data-screen-label="Why">
        <div class="section-heading">
          ${accentRailMarkup()}
          <h2>${data.h2}</h2>
        </div>
        ${paragraphs(data.intro)}
      </section>

      ${renderStepBlocks(data.steps)}

      ${faqSection("FAQ", data.faqs)}

      ${ctaBand({
    headline: "Want this handled for you?",
    body: "If your team is already busy, monthly review management keeps your replies on-brand, fast, and consistent while you focus on operations.",
    primaryHref: "/google-review-management",
    primaryLabel: "See review management",
    secondaryHref: "/website-audit-service",
    secondaryLabel: "See website audit",
    microcopy: "$149/month · Cancel anytime · No long-term contract"
  })}
    </main>
    ${siteFooter()}
  `;
}

const guideData: readonly GuidePageSpec[] = [
  {
    path: "/guides/respond-to-google-reviews",
    title: "How to Respond to Google Reviews (With Examples) | Foxmark",
    h1: "How to respond to Google reviews (with examples)",
    keyword: "how to respond to google reviews",
    description:
      "Learn how to respond to Google reviews with templates and examples and avoid common mistakes, build trust, and get more local calls from active customers.",
    h2: "How to respond to Google reviews without sounding fake",
    subhead:
      "Replying to reviews is not a social chore. It is your chance to protect your reputation while you are already visible.",
    intro: [
      "If you are searching for how to respond to Google reviews, start with a simple goal: every public reply should help the next person decide quickly whether to trust you. A good response gives context to what happened, thanks the reviewer, and answers the question the review raises.",
      "Most local businesses do two things wrong at first: they go silent after bad reviews, and they over-explain after good ones. Silence hurts rank signals and trust signals. Over-explaining makes your brand sound like a spokesperson instead of a real business. Both are fixable with a short process and a repeatable template."
    ],
    steps: [
      {
        heading: "Step 1: Use the same structure for every review",
        copy: [
          "Your first win is consistency. Use the same shape for every response. People reading multiple reviews on your profile can feel reliability in your tone before they read the details. That makes your profile easier to trust.",
          "A clean structure keeps your team moving fast when volume spikes. We use five short parts: thank, restate the issue briefly, give next action, clarify policy or boundary, and close with a friendly prompt. This does not sound robotic if your voice is plain and local. Keep replies short so the customer reads them at a glance."
        ],
        bullets: [
          "Say thanks first, even for negative reviews.",
          "Acknowledge one specific point from the review.",
          "State what you are doing next and who owns it.",
          "Invite a private follow-up for private details."
        ]
      },
      {
        heading: "Step 2: Handle positive reviews in a useful way",
        copy: [
          "Positive reviews are where you get extra upside. Your response is not about sounding modest; it is about reinforcing what people like and what they should expect next time they visit.",
          "Keep it short. One line of gratitude, one detail they praised, one invite to return. Do not add a full menu of discounts. The best reviews are for real customers, and those customers usually come back because your reply feels human, not promotional."
        ],
        examples: [
          {
            title: "Good reply to a 5-star review",
            reply:
              "Thank you for the kind words about our delivery speed. I’m glad we were on time and that your team felt informed. We are glad you are now moving to your next task, and we hope to serve you again this quarter."
          },
          {
            title: "Bad reply to a 5-star review",
            reply:
              "Thanks! We always try our best and are glad you liked it. We appreciate your support and recommend our package now!"
          }
        ]
      },
      {
        heading: "Step 3: Use a calm template for negative reviews",
        copy: [
          "Negative reviews are where teams either lose trust or build it. The highest-converting replies are calm, specific, and accountable. You are not trying to argue; you are trying to fix.",
          "Start by showing you understand the complaint, then move to action. If a fact is missing, ask the next step privately. If a request is not possible, say no with a clear reason and offer one useful alternative. In each case, end with a direct next touch.",
          "Use the template below as your default, then adjust one sentence to match your business language. If the issue is serious, include a named owner and an expected callback time."
        ],
        template:
          "Hi {name},\n\nThanks for the review. I am sorry this was not a good experience.\nI reviewed your details and shared them with our team lead.\nI will follow up by end of business day with the clear next step.",
        examples: [
          {
            title: "Good reply to a 1-star review",
            reply:
              "Thank you for flagging this. I am sorry the first visit did not meet your expectations. That is not the experience we want to deliver. I reviewed your case and I can take this directly to our service lead. Please send a direct message to support@foxmark.co so we can fix this and confirm next steps today."
          },
          {
            title: "Bad reply to a 1-star review",
            reply:
              "You had a bad experience. We are reviewing this and have shared your concern internally. Please stay patient."
          },
          {
            title: "Good reply to a delayed-response complaint",
            reply:
              "You are right. We posted late and should have replied faster. I am sorry for the delay. We are training a new response queue this week and are now aiming to respond within one business day. Thank you for the heads up and for giving us a chance to improve."
          },
          {
            title: "Bad reply to a delayed-response complaint",
            reply:
              "We are not sure why this happened. Maybe there was a mistake on Google. We can only do what we can from our side."
          }
        ]
      }
    ],
    faqs: [
      {
        question: "Should I reply to every Google review?",
        answer:
          "Yes. Quiet profiles look abandoned. A quick reply to positive, neutral, and negative reviews shows you are present and accountable. Even if you cannot fully solve a complaint in public, a short, honest first reply still helps.",
      },
      {
        question: "Can one template work for every review?",
        answer:
          "A template helps speed and quality, but you should still use one concrete line for the specific issue in each review. That is how your tone stays human while your response stays reliable under pressure.",
      },
      {
        question: "Is there a better response timing?",
        answer:
          "Same-day replies are strongest when possible, especially for negative reviews. Aim to acknowledge a complaint within 24 hours. It is better to send a sincere short reply on day one and follow with a fuller resolution update later."
      }
    ]
  },
  {
    path: "/guides/get-more-google-reviews",
    title: "How to Get More Google Reviews (Step-by-Step Guide) | Foxmark",
    h1: "How to get more Google reviews",
    keyword: "how to get more google reviews",
    description:
      "Learn how to get more Google reviews with a process: ask at the right moment, make the link easy, follow up once, and keep your process policy-compliant.",
    h2: "How to get more Google reviews without gaming your profile",
    subhead:
      "Review growth is not a trick. It is a process: ask well, make it easy, and follow up once.",
    intro: [
      "If you are asking how to get more Google reviews, the first question is who will ask, not what wording to copy. Ask people who already had a successful experience and who still have context from the visit.",
      "Trying to force reviews through random posting, social links, or hidden gates can backfire. Google does not reward pressure. It rewards relevance, clarity, and consistency. Your goal is a simple system your team can run every week."
    ],
    steps: [
      {
        heading: "Step 1: Ask at the right moment",
        copy: [
          "The highest-converting ask point is after a completed outcome. Right after checkout, quote sent, service call done, or project handoff, there is still enough energy for feedback and the details are fresh.",
          "Keep your ask phrase short. Mention one specific result and one simple next action. People respond better when they know exactly why you are asking and where their review helps. No pressure. No extra conditions. Just a direct request and a path to leave it."
        ],
        bullets: [
          "Ask within 15 minutes of the positive outcome when possible.",
          "Ask the person who had the best contact with the service outcome.",
          "Use plain wording: what we did, why their feedback matters, and where to go."
        ]
      },
      {
        heading: "Step 2: Make review posting easy",
        copy: [
          "A long instruction chain is the most common reason people never leave reviews. Your team should not ask people to search your business page, click multiple buttons, and then guess where to review.",
          "Use one direct review URL on business cards, SMS, receipts, and simple follow-up notes. Short links and buttons are better than paragraphs of explanation.",
          "For teams with a lot of repeat clients, include a one-line script so staff ask consistently. The script should say what happened, then give the exact link, and end with one sentence about your expectation for your business profile."
        ],
        examples: [
          {
            title: "Good direct review ask",
            reply:
              "Thanks for choosing us today. If you are happy with how we handled your request, could you share a 60-second Google review here? It helps real people trust the business before they call: https://g.page/...."
          },
          {
            title: "Better way to place the link",
            reply:
              "Put the link where people already look: after your invoice, in your SMS follow-up, and in your post-visit thank-you note. Keep the URL the same every time."
          }
        ]
      },
      {
        heading: "Step 3: Follow up once, stay policy-safe, and make it routine",
        copy: [
          "A gentle follow-up after one week is fine. If someone asked for more time, do not keep asking. If someone has a concern, answer it first and then invite review later.",
          "What not to do: do not offer discounts for reviews, do not gate access or perks until a review appears, and do not ask for reviews in exchange for payment reversals. Those practices risk reviews being removed or account issues. Policy-compliant processes are still strong if they are clear and simple.",
          "Reviews are trust signals, so keep an eye on your own process quality too. Track who asked, who responded, and what your team says changed. A weekly review cadence will usually show visible growth in a month, and review quality improves when teams fix repeat service gaps in public view."
        ],
        bullets: [
          "Do one gentle reminder after a week, then stop.",
          "Do not tie reviews to gift cards, discounts, or service upgrades.",
          "Do not hide poor feedback; a visible pattern can become a quick fix list."
        ]
      }
    ],
    faqs: [
      {
        question: "Can we ask customers for reviews through email automations?",
        answer:
          "Yes, as long as the message is clear and not manipulative. Keep it short, optional, and direct to the review page. A single follow-up with one ask is usually enough to stay policy-safe and respectful."
      },
      {
        question: "How soon after service should we send the ask?",
        answer:
          "Best timing is while the experience is still fresh, usually within one business day. If your team missed that window, ask as soon as possible with a simple and honest follow-up text or email."
      },
      {
        question: "Do we need a special team setup to scale this?",
        answer:
          "You only need one owner of the process and one short script to start. As you scale, add a weekly review tracker for asks sent, replies, and unresolved service issues. If you want this run weekly, monthly review management can handle this with a clear loop."
      }
    ]
  }
];

export const guideRoutes: PageRoute[] = guideData.map((page) => ({
  path: page.path,
  title: page.title,
  description: page.description,
  indexable: true,
  jsonLd: graph([
    articleNode({ headline: page.h1, description: page.description, path: page.path }),
    breadcrumbNode([
      { name: "Foxmark", path: "/" },
      { name: page.h1, path: page.path }
    ]),
    faqPageNode(page.faqs)
  ]),
  render: (assetBase: string) => renderGuidePage(page, assetBase)
}));
